/**
 * SettingsModal Component
 *
 * Modal for app settings including AI configuration,
 * appearance preferences, and account management.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Settings,
  Key,
  Palette,
  User,
  LogOut,
  Check,
  AlertCircle,
  Loader2,
  ExternalLink,
  Tag,
  Plus,
  Trash2,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TagBadge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useData } from '@/context/DataContext';
import { useApp } from '@/context/AppContext';
import { openRouter } from '@/services/openRouter';

// ============================================
// TYPES
// ============================================

export interface SettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
}

type SettingsTab = 'ai' | 'tags' | 'appearance' | 'account';

// ============================================
// TAB NAVIGATION
// ============================================

interface TabButtonProps {
  icon: typeof Settings;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ icon: Icon, label, isActive, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full',
        isActive
          ? 'bg-brand-50 text-brand-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// ============================================
// AI SETTINGS TAB
// ============================================

function AISettingsTab() {
  const [apiKey, setApiKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<'valid' | 'invalid' | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  // Load existing key on mount
  useEffect(() => {
    const existingKey = openRouter.getApiKey();
    if (existingKey) {
      setApiKey(existingKey);
      setIsSaved(true);
    }
  }, []);

  const handleValidateAndSave = async () => {
    if (!apiKey.trim()) {
      setValidationResult('invalid');
      return;
    }

    setIsValidating(true);
    setValidationResult(null);

    try {
      const isValid = await openRouter.validateKey(apiKey.trim());
      setValidationResult(isValid ? 'valid' : 'invalid');

      if (isValid) {
        openRouter.setApiKey(apiKey.trim());
        setIsSaved(true);
        // Persist to Drive in background -- don't let Drive errors affect validation UX
        openRouter.saveToDrive().catch(() => {
          console.warn('Failed to persist API key to Drive');
        });
      }
    } catch {
      setValidationResult('invalid');
    } finally {
      setIsValidating(false);
    }
  };

  const handleClearKey = async () => {
    openRouter.clearApiKey();
    await openRouter.clearFromDrive();
    setApiKey('');
    setIsSaved(false);
    setValidationResult(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">AI Configuration</h3>
        <p className="text-sm text-gray-500">Configure your AI assistant powered by OpenRouter.</p>
      </div>

      {/* API Key input */}
      <div className="space-y-2">
        <label htmlFor="api-key" className="block text-sm font-medium text-gray-700">
          OpenRouter API Key
        </label>
        <div className="flex gap-2">
          <Input
            id="api-key"
            type="password"
            value={apiKey}
            onChange={(e) => {
              setApiKey(e.target.value);
              setIsSaved(false);
              setValidationResult(null);
            }}
            placeholder="sk-or-v1-..."
            className="flex-1"
          />
          <Button onClick={handleValidateAndSave} disabled={isValidating || !apiKey.trim()}>
            {isValidating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isSaved && validationResult === 'valid' ? (
              <Check className="h-4 w-4" />
            ) : (
              'Save'
            )}
          </Button>
        </div>

        {/* Validation status */}
        {validationResult === 'valid' && (
          <p className="text-sm text-green-600 flex items-center gap-1">
            <Check className="h-4 w-4" />
            API key is valid and saved
          </p>
        )}
        {validationResult === 'invalid' && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="h-4 w-4" />
            Invalid API key. Please check and try again.
          </p>
        )}

        {/* Help text */}
        <p className="text-xs text-gray-500">
          Get your API key from{' '}
          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 hover:underline inline-flex items-center gap-0.5"
          >
            OpenRouter
            <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </div>

      {/* Current model info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Current Model</p>
        <p className="text-gray-900">{openRouter.getModelDisplayName()}</p>
        <p className="text-xs text-gray-500 mt-1">Model: {openRouter.getModel()}</p>
      </div>

      {/* Clear key button */}
      {isSaved && (
        <Button variant="outline" onClick={handleClearKey} className="text-red-600">
          Remove API Key
        </Button>
      )}
    </div>
  );
}

// ============================================
// TAGS SETTINGS TAB
// ============================================

function TagsSettingsTab() {
  const { config, addTag, removeTag, allItems } = useData();
  const [newTagName, setNewTagName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Get tag usage counts
  const tagUsageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allItems.forEach((item) => {
      item.tags.forEach((tag) => {
        counts[tag] = (counts[tag] ?? 0) + 1;
      });
    });
    return counts;
  }, [allItems]);

  const handleAddTag = () => {
    const trimmed = newTagName.trim();
    if (!trimmed) {
      setError('Tag name cannot be empty');
      return;
    }

    // Check for duplicates
    if (config.tags.some((t) => t.name.toLowerCase() === trimmed.toLowerCase())) {
      setError('This tag already exists');
      return;
    }

    addTag(trimmed);
    setNewTagName('');
    setError(null);
  };

  const handleRemoveTag = (tagName: string) => {
    // Check if tag is in use
    const usageCount = tagUsageCounts[tagName] ?? 0;
    if (usageCount > 0) {
      const confirmed = window.confirm(
        `"${tagName}" is used by ${usageCount} item${usageCount !== 1 ? 's' : ''}. ` +
          `Removing the tag definition will not remove it from existing items. Continue?`
      );
      if (!confirmed) return;
    }
    removeTag(tagName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Tag Management</h3>
        <p className="text-sm text-gray-500">
          Manage your tags. Tags help organize your items into categories.
        </p>
      </div>

      {/* Add new tag */}
      <div className="space-y-2">
        <label htmlFor="new-tag" className="block text-sm font-medium text-gray-700">
          Add New Tag
        </label>
        <div className="flex gap-2">
          <Input
            id="new-tag"
            value={newTagName}
            onChange={(e) => {
              setNewTagName(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Enter tag name..."
            className="flex-1"
            error={!!error}
          />
          <Button onClick={handleAddTag} disabled={!newTagName.trim()}>
            <Plus className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      {/* Existing tags */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Your Tags ({config.tags.length})
        </label>
        {config.tags.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
            No tags yet. Create your first tag above.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 border rounded-lg overflow-hidden">
            {config.tags.map((tag) => {
              const usageCount = tagUsageCounts[tag.name] ?? 0;
              const isSystemTag = tag.name.toLowerCase() === 'inbox';

              return (
                <li
                  key={tag.name}
                  className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50"
                >
                  <div className="flex items-center gap-3">
                    <TagBadge tag={tag.name} />
                    <span className="text-sm text-gray-500">
                      {usageCount} item{usageCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {!isSystemTag && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveTag(tag.name)}
                      className="h-8 w-8 text-gray-400 hover:text-red-500"
                      aria-label={`Delete ${tag.name} tag`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  {isSystemTag && <span className="text-xs text-gray-400 px-2">System tag</span>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================
// APPEARANCE SETTINGS TAB
// ============================================

function AppearanceSettingsTab() {
  const { view, setView } = useApp();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Appearance</h3>
        <p className="text-sm text-gray-500">Customize how Personal Insights looks.</p>
      </div>

      {/* Default view */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Default View</label>
        <div className="flex gap-2">
          <Button variant={view === 'grid' ? 'default' : 'outline'} onClick={() => setView('grid')}>
            Grid
          </Button>
          <Button variant={view === 'list' ? 'default' : 'outline'} onClick={() => setView('list')}>
            List
          </Button>
        </div>
      </div>

      {/* Theme (placeholder for future) */}
      <div className="space-y-2 opacity-50">
        <label className="block text-sm font-medium text-gray-700">Theme</label>
        <p className="text-sm text-gray-500">Theme customization coming soon.</p>
      </div>
    </div>
  );
}

// ============================================
// ACCOUNT SETTINGS TAB
// ============================================

function AccountSettingsTab({ onClose }: { onClose: () => void }) {
  const { user, signOut } = useAuth();

  const handleSignOut = () => {
    signOut();
    onClose();
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Account</h3>
        <p className="text-sm text-gray-500">Manage your account and data.</p>
      </div>

      {/* User info */}
      <div className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="h-12 w-12 rounded-full" />
        ) : (
          <div className="h-12 w-12 rounded-full bg-brand-100 flex items-center justify-center">
            <User className="h-6 w-6 text-brand-600" />
          </div>
        )}
        <div>
          <p className="font-medium text-gray-900">{user.name}</p>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
      </div>

      {/* Storage info */}
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Data Storage</p>
        <p className="text-sm text-gray-600">
          Your data is stored securely in your personal Google Drive. Only you have access to your
          files.
        </p>
      </div>

      {/* Sign out */}
      <Button variant="outline" onClick={handleSignOut} className="text-red-600 hover:bg-red-50">
        <LogOut className="h-4 w-4 mr-2" />
        Sign Out
      </Button>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('ai');

  // Reset to first tab when opening
  useEffect(() => {
    if (isOpen) {
      setActiveTab('ai');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Settings
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 overflow-hidden flex">
          {/* Tab navigation */}
          <nav className="w-48 flex-shrink-0 border-r pr-4 space-y-1">
            <TabButton
              icon={Key}
              label="AI Assistant"
              isActive={activeTab === 'ai'}
              onClick={() => setActiveTab('ai')}
            />
            <TabButton
              icon={Tag}
              label="Tags"
              isActive={activeTab === 'tags'}
              onClick={() => setActiveTab('tags')}
            />
            <TabButton
              icon={Palette}
              label="Appearance"
              isActive={activeTab === 'appearance'}
              onClick={() => setActiveTab('appearance')}
            />
            <TabButton
              icon={User}
              label="Account"
              isActive={activeTab === 'account'}
              onClick={() => setActiveTab('account')}
            />
          </nav>

          {/* Tab content */}
          <div className="flex-1 pl-6 overflow-y-auto">
            {activeTab === 'ai' && <AISettingsTab />}
            {activeTab === 'tags' && <TagsSettingsTab />}
            {activeTab === 'appearance' && <AppearanceSettingsTab />}
            {activeTab === 'account' && <AccountSettingsTab onClose={onClose} />}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
